import { useId, type ReactNode } from "react";

/**
 * The one instant tooltip for the whole app: a dotted-underline hint that
 * shows its text on hover and on keyboard focus, with no delay (never a
 * native `title`, which is slow and unstyled). Wraps its trigger, so any
 * inline text or icon can carry a tooltip without repeating the markup
 * StatusBar first established for the accuracy readout (GRA-95).
 *
 * Right-aligned by default so a tooltip near the right edge of the header
 * (the common case here) doesn't clip; pass `align="left"` for triggers
 * closer to the left edge of their container.
 *
 * `interactive` is for wrapping something already focusable (a button):
 * the wrapper skips its own tab stop and cursor-help/underline styling (the
 * child supplies both), and keyboard visibility switches from
 * `group-focus-visible` (the wrapper itself focused) to `group-focus-within`
 * (focus landed on the child instead) so a single Tab still reveals it.
 */
export type TooltipProps = {
  /** The tooltip's text. */
  text: string;
  /** The element the tooltip is attached to. */
  children: ReactNode;
  /** Extra classes for the trigger wrapper (e.g. to add color or margin). */
  className?: string;
  /** Which edge the tooltip panel hangs from. Defaults to "right". */
  align?: "left" | "right";
  /** Set when children is already an interactive element (e.g. a button). */
  interactive?: boolean;
};

export function Tooltip({
  text,
  children,
  className = "",
  align = "right",
  interactive = false,
}: TooltipProps) {
  const id = useId();
  const alignClass = align === "left" ? "left-0" : "right-0";
  const triggerClass = interactive
    ? "group relative"
    : "group relative cursor-help underline decoration-muted/50 decoration-dotted underline-offset-2";
  const focusClass = interactive ? "group-focus-within:opacity-100" : "group-focus-visible:opacity-100";

  return (
    <span
      className={`${triggerClass} ${className}`}
      tabIndex={interactive ? undefined : 0}
      aria-describedby={id}
    >
      {children}
      <span
        id={id}
        role="tooltip"
        className={`pointer-events-none absolute top-full z-10 mt-1 whitespace-nowrap rounded border border-muted/30 bg-surface px-2 py-1 text-xs text-fg opacity-0 group-hover:opacity-100 ${focusClass} ${alignClass}`}
      >
        {text}
      </span>
    </span>
  );
}
